import {useWidth} from '@hconnect/common/hooks/useWidth'
import {EventsTab, SortOrder} from '@hconnect/common/types'
import {formatDateTime} from '@hconnect/common/utils/datetime.utils'
import {Column, PaginationOptions} from '@hconnect/uikit/src/lib2'
import {
  AllFilterOptions,
  WorkOrderSortBy,
  pmNotificationFilterOptions,
  FilterOptions,
  PmNotificationStatusLabel
} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import {isEqual} from 'lodash'
import React, {FC, useState} from 'react'

import {ShiftEventsFilterBar} from '../components/filter'
import {
  generateWorkOrderEquipmentNumberColumn,
  getTitleColumn,
  Label,
  Secondary
} from '../components/tableHelpers'
import {ColumnsMap, Notification} from '../types'

import {EventsListView} from './EventsListView'
import {NotificationSideCard} from './NotificationSideCard'

const generateColumnsMap = (t: TFunction, freeSearchText?: string): ColumnsMap<Notification> => {
  const idColumn: Column<Notification> = {
    key: 'id',
    label: <Label>{t('shiftEvent.label.NotificationId')}</Label>,
    customTemplate: ({id}) => <Secondary>{id}</Secondary>
  }
  const createdAtColumn: Column<Notification> = {
    key: 'createdAt',
    label: <Label>{t('shiftEvent.label.createdAt')}</Label>,
    customTemplate: ({createdAt}) => (
      <Secondary sx={{whiteSpace: 'nowrap'}}>{formatDateTime(createdAt)}</Secondary>
    )
  }
  const technicalIdColumn: Column<Notification> = {
    key: 'technicalId',
    label: <Label>{t('shiftEvent.label.TechIdNo')}</Label>,
    customTemplate: ({equipment}) => <Secondary>{equipment?.technicalId}</Secondary>
  }
  const generateStatusColumn = (type?: 'small' | 'regular'): Column<Notification> => ({
    headProps: type === 'small' ? {style: {paddingLeft: '4px'}} : {},
    key: 'status',
    sortable: true,
    width: type === 'small' ? 0 : undefined,
    label: <Label>{t('shiftEvent.label.status')}</Label>,
    customTemplate: (item: Notification) => (
      <Secondary>
        <PmNotificationStatusLabel status={item.status} iconOnly={type === 'small'} />
      </Secondary>
    )
  })

  return {
    XS: [idColumn, getTitleColumn(t, freeSearchText), createdAtColumn],
    S: [idColumn, getTitleColumn(t, freeSearchText), createdAtColumn],
    M: [
      idColumn,
      getTitleColumn(t, freeSearchText),
      createdAtColumn,
      generateWorkOrderEquipmentNumberColumn('small', t, freeSearchText),
      technicalIdColumn,
      generateStatusColumn('regular')
    ],
    L: [
      idColumn,
      getTitleColumn(t, freeSearchText),
      createdAtColumn,
      generateWorkOrderEquipmentNumberColumn('regular', t, freeSearchText),
      technicalIdColumn,
      generateStatusColumn('regular')
    ],
    XL: [
      idColumn,
      getTitleColumn(t, freeSearchText),
      createdAtColumn,
      generateWorkOrderEquipmentNumberColumn('regular', t, freeSearchText),
      technicalIdColumn,
      generateStatusColumn('regular')
    ]
  }
}

type NotificationsListViewProps = {
  activeTab: EventsTab
  setActiveTab: (tab: EventsTab) => void
  activeFilter: AllFilterOptions
  events?: Notification[]
  isLoading?: boolean
  showBottomProgress?: boolean
  onFilterChange: (next: FilterOptions) => void
  paginationOptions?: PaginationOptions
  plantId: string
}

export const NotificationsListView: FC<NotificationsListViewProps> = (props) => {
  const breakPoint = useWidth()
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const [isMobileFilterOpen, openMobileFilter] = useState(false)

  const onSort = (sortBy: string, sortOrder?: SortOrder) => {
    const next = {
      ...props.activeFilter,
      sortNotificationsBy: sortBy as WorkOrderSortBy,
      notifSortDir: sortOrder
    }
    if (!sortBy || !sortOrder || isEqual(props.activeFilter, next)) {
      return
    }
    props.onFilterChange(next)
  }

  return (
    <EventsListView<Notification, WorkOrderSortBy>
      activeTab={props.activeTab}
      setActiveTab={props.setActiveTab}
      activeFilter={props.activeFilter}
      events={props.events}
      isLoading={props.isLoading}
      paginationOptions={props.paginationOptions}
      plantId={props.plantId}
      urlParamId="notificationId"
      generateColumnsMap={(t) => generateColumnsMap(t, props.activeFilter.freeText)}
      SideCardComponent={NotificationSideCard}
      sortedBy={props.activeFilter.sortNotificationsBy}
      sortDirection={props.activeFilter.notifSortDir}
      onSort={onSort}
      headerActionContent={
        <ShiftEventsFilterBar
          activeSettings={props.activeFilter}
          isCollapsible={isSmall}
          onChange={props.onFilterChange}
          openMobileFilter={openMobileFilter}
          isMobileFilterOpen={isMobileFilterOpen}
          plantId={props.plantId}
          options={pmNotificationFilterOptions}
          defaultFilters={['timeRange']}
        />
      }
    />
  )
}
